import type { GateDenied } from '../../../api/request'
import { t } from '../../../i18n'
import { openBotLink } from '../../../lib/telegram'

interface Props {
  denied: GateDenied
  onClose: () => void
}

/**
 * 门禁引导底部弹层。
 *
 * 403 是正常的 UI 状态，**不是错误**：这里渲染「加入频道 / 开启提醒」的引导，
 * 绝不能弹成红色报错——那会让用户以为出故障了。
 */
export function GateSheet({ denied, onClose }: Props) {
  const copy = textFor(denied.gate)
  const target = denied.gate === 'need_channel' ? denied.channelUrl : denied.botStartUrl

  return (
    <div style={styles.backdrop} onClick={onClose} role="presentation">
      <div style={styles.sheet} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal>
        <h3 style={styles.title}>{copy.title}</h3>
        <p style={styles.body}>{copy.body}</p>
        <button
          style={styles.primary}
          disabled={!target}
          onClick={() => target && openBotLink(target)}
        >
          {copy.action}
        </button>
      </div>
    </div>
  )
}

function textFor(gate: GateDenied['gate']) {
  switch (gate) {
    case 'need_channel':
      return {
        title: t.gate.needChannelTitle,
        body: t.gate.needChannelBody,
        action: t.gate.needChannelAction,
      }
    case 'need_bot':
      return { title: t.gate.needBotTitle, body: t.gate.needBotBody, action: t.gate.needBotAction }
    case 'need_app':
      return { title: t.gate.needAppTitle, body: t.gate.needAppBody, action: t.gate.needAppAction }
    default:
      return { title: '', body: '', action: '' }
  }
}

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,.6)',
    display: 'flex',
    alignItems: 'flex-end',
    // 比选集抽屉（20）高：门禁引导是必须先处理的那一层
    zIndex: 30,
  },
  // 白底浅色，和选集面板、倍速弹层一个样子；主按钮是 Nova 渐变，和 App 的 PrimaryButton 一样
  sheet: {
    width: '100%',
    background: 'var(--card)',
    color: 'var(--ink)',
    borderRadius: '16px 16px 0 0',
    padding: '22px 20px calc(22px + env(safe-area-inset-bottom))',
  },
  title: { margin: '0 0 8px', fontSize: 17, fontWeight: 700 },
  body: { margin: '0 0 18px', fontSize: 14, color: 'var(--ink-2)' },
  primary: {
    width: '100%',
    height: 48,
    background: 'var(--accent-gradient)',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
  },
} satisfies Record<string, React.CSSProperties>
